/* ---------------------------------------------------------------------------
   The register's map: Nepal, with the located structures on it.

   Three layers, bottom to top, all sharing one projection (./mercator.ts) so
   they line up:

     1. Raster tiles — the map proper — from whichever of the two base maps the
        user has chosen: OpenStreetMap's standard style, or OpenTopoMap's contours
        and hillshading over the same data. Both free, no key, no account, and no
        library: a slippy map is a grid of 256 px images at computed offsets, and
        hand-rolling that is about eighty lines against the ~150 kB Leaflet would
        add to a bundle whose only dependency is three.js. See TILE_SOURCES, which
        keeps each source's URL and its required credit in one place.
     2. The national boundary, from src/data/nepal.ts — a thin border over the
        tiles, and the WHOLE map when the tiles do not arrive. A bridge inspector
        on a hillside with one bar of signal gets an outline with the structures
        in their right places rather than an empty grey rectangle.
     3. The markers, one per located structure, each a button that opens its
        record.

   A PURE FUNCTION OF STATE, like every other screen here. Pan and zoom are
   `store.set` calls (`mapView`), and the render redraws from them; the store
   coalesces to one paint per frame, so a wheel spin cannot outrun it. The one
   thing state cannot know is how big the box is, so the box measures itself and
   reports it (`mapResized`) — after which the tile grid and the home fit are
   functions of state again.

   Dragging is the exception, and only while the pointer is down: it translates
   the layers with a CSS transform and commits the new centre on release. Going
   through state per pointermove would be a repaint of the whole shell per frame
   for a gesture whose whole job is to feel immediate.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, can, store, type MapPlacement, type MapView, type State } from '../core/store.ts';
import { button, card, cardHead } from '../components/widgets.ts';
import { findBridge, heroModel, shortName } from '../data/derive.ts';
import { active } from '../tenant/registry.ts';
import { BANDS, bandCounts, bandLabel, screenBridge } from '../data/screening.ts';
import type { BridgeView, MapBase, MapPaint, ModelSummary } from '../data/types.ts';
import { boundsOf, fitBounds, padBounds, project, TILE, tilesFor, unproject } from './mercator.ts';
import { deckFootprint, normaliseAxis } from './deck.ts';
import { sceneStatus } from './scene3d.ts';
import { flyStatus } from './flyMap.ts';
import { buildModelGeometry } from '../modeler/geometry.ts';
import { join, metres } from '../data/recorded.ts';
import { chainage } from '../tenant/format.ts';
import { decidedLine, operationalStatus } from '../data/posting.ts';

/**
 * How far out the map will go.
 *
 * Far enough to show Nepal and its neighbours in one box; there is no reason to
 * leave the region in a register of Nepalese structures. How far IN is per tile
 * source — see `maxZoom` on each — because the two do not serve the same levels
 * and a map that asks for a level its source has not got shows broken tiles.
 */
const MIN_ZOOM = 5;

/** Least extent the home view will fit, in degrees — see fitHome. */
const MIN_HOME_SPAN = 0.6;

/**
 * Zoom at which deck footprints start being drawn.
 *
 * Below this a 65 m bridge is under two pixels: a shape too small to read as one,
 * and one more thing under the pin. The footprint is there to be looked at, so it
 * appears when there is something to look at.
 */
const DECK_MIN_ZOOM = 14;

/** One line of a base map's credit; `href` makes it a link. */
export interface CreditPart {
  text: string;
  href?: string;
}

/** A base map the user can choose between. */
export interface TileSource {
  /** Button label — short, because the two sit side by side over the map. */
  label: string;
  /** What the button says it will do. */
  title: string;
  /**
   * Tile URL templates, in the `{z}/{x}/{y}` form every map library speaks.
   *
   * The template is the primary form and `url()` is derived from it, so the
   * hand-rolled tile layer and MapLibre's raster source cannot end up pointing at
   * different servers. More than one entry means subdomains — OpenTopoMap
   * documents three, and a library that takes the array spreads its requests
   * across them.
   */
  tiles: readonly string[];
  /**
   * Deepest zoom this source serves.
   *
   * Not decoration: asking a tile server for a level it does not render returns
   * an error tile or a 404, which here means the whole map falls back to the
   * outline. It is also what "the closest possible view" resolves to when a
   * structure is clicked, so it decides how close that gets.
   */
  maxZoom: number;
  /**
   * The credit this source requires, as parts so the links stay links.
   *
   * Beside the URL rather than anywhere else on purpose: the licence belongs to
   * the tiles, and a switch of source that did not switch the credit would be
   * showing one provider's map under another's name. Both of these are stricter
   * than "be nice" — OSM's tile policy asks for attribution, and OpenTopoMap's
   * style is CC-BY-SA, which requires naming it and its licence.
   */
  credit: readonly CreditPart[];
}

const OSM_COPYRIGHT = 'https://www.openstreetmap.org/copyright';

/**
 * The two base maps, and everything that differs between them.
 *
 * Both are free, keyless and rendered from the same OpenStreetMap data. What
 * differs is the styling and, for `topo`, an extra data source: OpenTopoMap draws
 * SRTM elevation as 20 m contours with 100 m index lines and hillshaded relief,
 * which is what makes it worth having on a register of river crossings in hill
 * country. It is a donation-funded volunteer server — slower than OSM's, and not
 * to be hammered — which is one more reason the map fetches only the tiles its box
 * needs and reuses them across repaints.
 *
 * NEITHER IS A SOURCE OF ELEVATION FOR ENGINEERING. SRTM is roughly 30 m ground
 * sampling; the contours read the shape of a valley, and have nothing to say about
 * a `deckRL` recorded to the centimetre.
 */
export const TILE_SOURCES: Record<MapBase, TileSource> = {
  street: {
    label: 'Street',
    title: 'OpenStreetMap standard — roads, rivers and settlements',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    // OSM's standard style renders to 19 everywhere, which is close enough to
    // read a deck against its abutments.
    maxZoom: 19,
    credit: [{ text: '© OpenStreetMap contributors', href: OSM_COPYRIGHT }],
  },
  topo: {
    label: 'Topo',
    title: 'OpenTopoMap — contour lines and hillshading over the same data',
    // The three hosts OpenTopoMap documents.
    tiles: [
      'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
    ],
    // OpenTopoMap stops at 17. Past that its tiles 404, which would take the map
    // down to the outline — so a view deeper than this is clamped, not requested.
    maxZoom: 17,
    credit: [
      { text: 'map data © OpenStreetMap contributors, SRTM', href: OSM_COPYRIGHT },
      { text: 'style © OpenTopoMap (CC-BY-SA)', href: 'https://opentopomap.org/' },
    ],
  },
};

/** The source in force, tolerating a persisted value from another build. */
export function sourceOf(base: MapBase): TileSource {
  return TILE_SOURCES[base] ?? TILE_SOURCES.street;
}

/**
 * One tile's URL, from the source's own template.
 *
 * Derived rather than written twice: the hand-rolled layer and MapLibre's raster
 * source share the templates in TILE_SOURCES, so they cannot drift onto different
 * servers or different licences. Which of several subdomains is chosen is decided
 * by the tile, not at random — the same tile always asks the same host, so a
 * browser cache and a repaint find it where they left it.
 */
export function tileUrl(base: MapBase, z: number, x: number, y: number): string {
  const templates = sourceOf(base).tiles;
  const template = templates[(x + y) % templates.length] ?? templates[0]!;
  return template
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y));
}

/* --------------------------- the view --------------------------- */

/** Structures that can be put on a map at all. */
export function located(list: readonly BridgeView[]): (BridgeView & { lat: number; lng: number })[] {
  return list.filter(
    (b): b is BridgeView & { lat: number; lng: number } => b.lat != null && b.lng != null,
  );
}

/**
 * The home view: the located structures, framed.
 *
 * Not the whole country. Five bridges inside a 90 km circle would be five pins on
 * top of each other at national zoom, and "where is the network" is answered
 * better by the region they are actually in — the Nepal button is there for the
 * other question. Falls back to the country when nothing is located, which is
 * also what an empty register gets.
 */
export function fitHome(list: readonly BridgeView[], width: number, height: number): MapView {
  const bounds = boundsOf(located(list));
  /* Nothing located, so there is nothing to fit — which is the whole DPWH
     register today. Falls back to the department's own opening view where its
     profile states one, and to its jurisdiction where it does not. A national
     department whose register covers three highway sections should not open
     zoomed out across two thousand kilometres of ocean; see `home` in
     src/tenant/types.ts. */
  if (!bounds) {
    const geo = active().geo;
    return fitBounds(geo.home ?? geo.bounds, width, height);
  }
  return fitBounds(padBounds(bounds, MIN_HOME_SPAN), width, height, 56);
}

/** How far in the current source will go. */
export function maxZoomOf(base: MapBase): number {
  return sourceOf(base).maxZoom;
}

export function clampZoom(zoom: number, base: MapBase): number {
  return Math.max(MIN_ZOOM, Math.min(maxZoomOf(base), zoom));
}

/** The whole jurisdiction in the box — what the country button asks for. */
export function fitCountry(width: number, height: number): MapView {
  return fitBounds(active().geo.bounds, width, height);
}

/**
 * As close as this source will get to one structure — what a click on it asks for.
 *
 * "Closest possible" is a property of the tile source, not of the map: the street
 * layer renders to 19 and the topographic one stops at 17, so the same click gets
 * you nearer on one than the other. The stored view is left unclamped on purpose
 * (see currentView): switch to Topo at 19 and back, and you are at 19 again rather
 * than stuck at the level the other source could manage.
 */
export function closestView(b: { lat: number; lng: number }, base: MapBase): MapView {
  return { lat: b.lat, lng: b.lng, zoom: maxZoomOf(base) };
}

/**
 * The view actually on screen: what state says, or home — clamped to what the
 * current source can serve.
 *
 * The clamp is here rather than at every place a view is set, so that a view is a
 * WISH and this is what is drawable of it. That is what lets the base switch keep
 * the view across a source that cannot show it that closely.
 */
function currentView(s: Readonly<State>, list: readonly BridgeView[]): MapView {
  const view = s.mapView ?? fitHome(list, s.mapSize.width, s.mapSize.height);
  return { ...view, zoom: clampZoom(view.zoom, s.mapBase) };
}

/* --------------------------- geometry helpers --------------------------- */

/**
 * Where a coordinate lands inside the map box, in CSS pixels from its top-left.
 *
 * The box shows a `width` × `height` window onto the world square, centred on the
 * view — so a point's offset is its world position minus the window's origin.
 * Every layer is placed with this, which is what keeps them on top of each other.
 */
export function toBox(
  lng: number,
  lat: number,
  view: MapView,
  width: number,
  height: number,
): { left: number; top: number } {
  const centre = project(view.lng, view.lat, view.zoom);
  const p = project(lng, lat, view.zoom);
  return { left: p.x - centre.x + width / 2, top: p.y - centre.y + height / 2 };
}

/** Pixels dragged, as a change of centre at this zoom. */
export function panned(view: MapView, dx: number, dy: number): MapView {
  const c = project(view.lng, view.lat, view.zoom);
  const moved = unproject(c.x - dx, c.y - dy, view.zoom);
  return { ...moved, zoom: view.zoom };
}

/**
 * Zoom by `delta` levels, keeping the point under the cursor where it is.
 *
 * The anchor is what makes a wheel zoom feel like a map rather than a slideshow:
 * the coordinate under the pointer before the zoom has to be under it after, so
 * the new centre is worked out from that fixed point rather than from the old
 * centre. Anchoring at the box centre gives the buttons' plain zoom.
 */
export function zoomed(
  view: MapView,
  delta: number,
  anchor: { x: number; y: number },
  width: number,
  height: number,
  base: MapBase,
): MapView {
  const zoom = clampZoom(view.zoom + delta, base);
  if (zoom === view.zoom) return view;

  // The coordinate under the anchor, before.
  const before = project(view.lng, view.lat, view.zoom);
  const at = unproject(
    before.x + (anchor.x - width / 2),
    before.y + (anchor.y - height / 2),
    view.zoom,
  );

  // Put it back under the anchor, after.
  const p = project(at.lng, at.lat, zoom);
  const centre = unproject(p.x - (anchor.x - width / 2), p.y - (anchor.y - height / 2), zoom);
  return { ...centre, zoom };
}

/* --------------------------- layers --------------------------- */

/**
 * The tile grid.
 *
 * `z` is the view's zoom floored — tiles only exist at integer levels — and the
 * fraction left over is applied as a scale on the level, which is how a fitted
 * view lands between two tile levels without jumping to one of them.
 *
 * A `data-keep` slot, kept up by syncTiles after each paint. src/core/dom.ts
 * matches by position, and a zoom changes how many tiles there are: every image
 * after the first changed column would be handed another tile's `src` and go
 * blank while it fetched — the whole map flickering on every wheel notch. Here a
 * tile is matched by its x/y and only moved. The slot is drawn whole the first
 * time, and again whenever the base changes, because the keep value names it.
 */
function tileLayer(base: MapBase, view: MapView, width: number, height: number): HTMLElement {
  const slot = h('div', {
    class: 'nmap__tiles',
    'data-keep': `tiles-${base}`,
    'data-base': base,
    'data-view': `${view.lng} ${view.lat} ${view.zoom}`,
    'data-size': `${width} ${height}`,
  });
  slot.appendChild(drawLevel(document.createElement('div'), base, view, width, height));
  return slot;
}

/** The level a view draws its tiles from. */
function levelOf(view: MapView): number {
  return Math.max(0, Math.floor(view.zoom));
}

/**
 * Make one level of tiles match the view: tiles it already has are moved, the
 * rest are fetched, and the ones that have left the box are dropped.
 *
 * `draggable: false` matters: a native image drag would hijack the pan the moment
 * the pointer moved over a tile, which is everywhere.
 */
function drawLevel(
  level: HTMLElement,
  base: MapBase,
  view: MapView,
  width: number,
  height: number,
): HTMLElement {
  const z = levelOf(view);
  const scale = 2 ** (view.zoom - z);
  const { tiles } = tilesFor(view, z, width / scale, height / scale);
  placeLevel(level, z, scale, width, height);

  const have = new Map<string, HTMLImageElement>();
  for (const img of Array.from(level.querySelectorAll<HTMLImageElement>('img'))) {
    have.set(img.dataset['tile'] ?? '', img);
  }
  for (const t of tiles) {
    const key = `${t.x}/${t.y}`;
    let img = have.get(key);
    if (img) have.delete(key);
    else {
      img = document.createElement('img');
      img.className = 'nmap__tile';
      img.alt = '';
      img.draggable = false;
      img.dataset['tile'] = key;
      /* One failed tile is enough to know: they all come from one host, so the
         first error means the host is unreachable — or blocked, which looks
         the same from here. The map falls back to the outline rather than
         filling with broken-image icons. */
      img.addEventListener('error', () => actions.mapTilesFailed());
      img.addEventListener('load', () => dropCovered(level));
      img.src = tileUrl(base, t.z, t.x, t.y);
      level.appendChild(img);
    }
    img.style.left = `${t.left}px`;
    img.style.top = `${t.top}px`;
  }
  for (const gone of have.values()) gone.remove();
  return level;
}

/** Size and scale a level's box for the view, at its own tile level `z`. */
function placeLevel(level: HTMLElement, z: number, scale: number, width: number, height: number): void {
  level.className = 'nmap__level';
  level.dataset['z'] = String(z);
  level.style.width = `${width / scale}px`;
  level.style.height = `${height / scale}px`;
  level.style.transform = `scale(${scale})`;
}

/**
 * Once the level on top has every tile, the levels kept under it go.
 *
 * Crossing a whole level replaces every tile at once, and until they arrive the
 * last level stays underneath, scaled to the new view — a softer map for a
 * moment rather than an empty one.
 */
function dropCovered(level: HTMLElement): void {
  const slot = level.parentElement;
  if (!slot || slot.lastElementChild !== level) return;
  if (!Array.from(level.querySelectorAll('img')).every((img) => img.complete)) return;
  while (slot.firstElementChild !== level) slot.firstElementChild!.remove();
}

/** Keep the live tile slot in step with the view the last paint drew. */
function syncTiles(): void {
  const slot = document.querySelector<HTMLElement>('.nmap__tiles[data-keep]');
  if (!slot) return;
  const [lng, lat, zoom] = (slot.dataset['view'] ?? '').split(' ').map(Number);
  const [width, height] = (slot.dataset['size'] ?? '').split(' ').map(Number);
  if (lng == null || lat == null || zoom == null || width == null || height == null) return;
  const view: MapView = { lng, lat, zoom };
  const z = levelOf(view);

  const levels = Array.from(slot.children) as HTMLElement[];
  let current = levels.find((l) => l.dataset['z'] === String(z));
  if (!current) current = document.createElement('div');
  drawLevel(current, slot.dataset['base'] as MapBase, view, width, height);

  /* The levels underneath are only stand-ins: re-placed for this view, and gone
     when more than one level away — a tile scaled four times is no help. */
  for (const l of levels) {
    if (l === current) continue;
    const lz = Number(l.dataset['z']);
    if (Math.abs(lz - z) > 1) {
      l.remove();
      continue;
    }
    const scale = 2 ** (zoom - lz);
    const c = project(lng, lat, lz);
    const originX = c.x - width / scale / 2;
    const originY = c.y - height / scale / 2;
    placeLevel(l, lz, scale, width, height);
    for (const img of Array.from(l.querySelectorAll<HTMLImageElement>('img'))) {
      const [x, y] = (img.dataset['tile'] ?? '').split('/').map(Number);
      img.style.left = `${x! * TILE - originX}px`;
      img.style.top = `${y! * TILE - originY}px`;
    }
  }
  slot.appendChild(current);
  dropCovered(current);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Everything vector, in one SVG: the national boundary, and the deck footprints.
 *
 * One element rather than two because they share a projection and a viewBox, and
 * because the order they are drawn in is the order they are written here — the
 * country behind, the structures in front of it, the pins in front of both.
 *
 * `pointer-events: none` on the whole layer (see app.css): a footprint is
 * something to look at, and the pin over it is the thing to click. Nothing here
 * may intercept a drag either.
 */
function vectorLayer(
  s: Readonly<State>,
  pins: readonly (BridgeView & { lat: number; lng: number })[],
  view: MapView,
  width: number,
  height: number,
): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'nmap__vectors');
  svg.setAttribute('viewBox', `0 0 ${Math.round(width)} ${Math.round(height)}`);
  svg.setAttribute('aria-hidden', 'true');

  /* THE NATIONAL OUTLINE, WHERE THE DEPARTMENT HAS ONE BUNDLED. Nepal does: 267
     points regenerated from Natural Earth, which is what the map is made of when
     no tile arrives. The Philippines does not, and the reason is not laziness —
     see the header of src/data/philippines.ts. A profile with no outline gets no
     path rather than a coastline invented to fill the space, and the map then
     shows the tiles, the pins and (offline) the credit line saying it has
     neither. */
  const outline = active().geo.outline;
  if (outline) {
    const border = document.createElementNS(SVG_NS, 'path');
    border.setAttribute('class', 'nmap__border');
    border.setAttribute(
      'd',
      `${outline
        .map(([lng, lat], i) => {
          const { left, top } = toBox(lng, lat, view, width, height);
          return `${i === 0 ? 'M' : 'L'}${left.toFixed(1)} ${top.toFixed(1)}`;
        })
        .join('')}Z`,
    );
    svg.appendChild(border);
  }

  /* Footprints only once they are big enough to read, and only for structures
     with a known axis — see src/map/deck.ts, which draws nothing rather than
     guess which way a bridge points. */
  if (view.zoom >= DECK_MIN_ZOOM) {
    const draft = s.mapPlacing;
    for (const b of pins) {
      /* While a structure is being placed, its footprint comes from the DRAFT and
         not from the record — that shape following the pointer is the whole point
         of the gesture. Every other structure is drawn as stored. */
      const corners =
        draft?.bridgeId === b.id
          ? draft.bearing == null
            ? null
            : deckFootprint(b, { lat: draft.lat, lng: draft.lng, bearing: draft.bearing })
          : deckFootprint(b);
      if (!corners) continue;
      const poly = document.createElementNS(SVG_NS, 'polygon');
      poly.setAttribute(
        'points',
        corners
          .map(([lng, lat]) => {
            const { left, top } = toBox(lng, lat, view, width, height);
            return `${left.toFixed(1)},${top.toFixed(1)}`;
          })
          .join(' '),
      );
      poly.setAttribute(
        'class',
        [
          'nmap__deck',
          b.id === s.selectedId ? 'nmap__deck--on' : null,
          draft?.bridgeId === b.id ? 'nmap__deck--placing' : null,
        ]
          .filter(Boolean)
          .join(' '),
      );
      svg.appendChild(poly);
    }
  }

  return svg;
}

/**
 * One structure, as a pin and a label.
 *
 * A button, not a div: it navigates, so it has to be reachable by keyboard and
 * announce itself as an action. The title carries what the label cannot — the
 * coordinate it is drawn from, so anyone who thinks a pin is in the wrong place
 * can see the number it came from.
 */
function marker(
  b: BridgeView & { lat: number; lng: number },
  s: Readonly<State>,
  view: MapView,
  width: number,
  height: number,
): HTMLElement | null {
  const { left, top } = toBox(b.lng, b.lat, view, width, height);
  // Off screen by more than a label's width: nothing to draw, and one fewer
  // node under the pointer.
  if (left < -160 || top < -80 || left > width + 160 || top > height + 80) return null;

  const on = b.id === s.selectedId;
  /* Posted or closed, and on whose decision (LC-23) — on the pin, not only the record. */
  const status = operationalStatus(b);

  /* Screened on every paint rather than cached: it is a handful of comparisons
     over fields already in memory, and a cache would be one more thing to
     invalidate the day somebody finally fills in a foundation type. */
  const screening = s.mapPaint === 'screening' ? screenBridge(b) : null;

  return h(
    'button',
    {
      type: 'button',
      class: [
        'nmap__pin',
        on && 'nmap__pin--on',
        screening && `nmap__pin--${screening.overall}`,
        status.kind === 'posted' || status.kind === 'closed' ? `nmap__pin--${status.kind}` : null,
      ],
      'data-status': status.kind,
      style: { left: `${left.toFixed(1)}px`, top: `${top.toFixed(1)}px` },
      title:
        `${b.id} — ${b.name}\n` +
        `${join([b.route, chainage(b.km)], ' ')} · ` +
        `${join([b.spans == null ? null : `${b.spans} span(s)`, metres(b.length)], ', ')}\n` +
        `${b.lat.toFixed(6)}, ${b.lng.toFixed(6)} — map pin, not a surveyed position\n` +
        `${status.label} — ${decidedLine(status)}\n` +
        (screening
          ? `Screening: ${bandLabel(screening.overall).toLowerCase()} — ` +
            screening.facets.map((f) => `${f.label.toLowerCase()} ${f.band}`).join(', ') +
            '\n'
          : '') +
        (on ? 'Click again to open the record' : 'Click to zoom to it'),
      onclick: () => selectOnMap(b, s),
    },
    h('span', { class: 'nmap__dot', 'aria-hidden': 'true' }),
    h('span', { class: 'nmap__label', text: shortName(b) }),
  );
}

/**
 * What clicking a structure does, wherever it is clicked from.
 *
 * Two steps, because there are two things someone means by it and the map is on
 * screen for one of them: the first click brings the map down to the structure as
 * close as the tiles go, and the second — on the one already selected — opens its
 * record. Shared with the route strips below the map (src/screens/network.ts), so
 * a pin and a strip marker behave the same way.
 *
 * A structure with no coordinates has nothing to zoom to, so it goes straight to
 * its record rather than appearing to do nothing.
 */
export function selectOnMap(b: BridgeView, s: Readonly<State>): void {
  if (b.lat == null || b.lng == null) {
    actions.openBridge(b.id);
    return;
  }
  if (b.id === s.selectedId && s.mapView !== null) {
    actions.openBridge(b.id);
    return;
  }
  actions.focusBridgeOnMap(b.id, closestView({ lat: b.lat, lng: b.lng }, s.mapBase));
}

/* --------------------------- interaction --------------------------- */

/**
 * Whether a pointer press on this element should start a pan.
 *
 * The controls, the pins and the credit link sit INSIDE the stage — they float
 * over the map, which is where they belong — so the stage's own pointerdown sees
 * their presses too. Every one of them has its own behaviour, and a press on one
 * is not a press on the map.
 *
 * This is a guard against a specific, invisible failure rather than tidiness.
 * `beginPan` used to take pointer capture on pointerdown regardless of what was
 * pressed; capture retargets the pointerup to the capturing element, which makes
 * the nearest common ancestor of down and up the stage rather than the button —
 * and a `click` event is dispatched at that ancestor. So every control and every
 * pin looked live, highlighted on hover, and did nothing at all when clicked.
 */
export function startsPan(target: Element | null): boolean {
  return (
    target?.closest('.nmap__btn, .nmap__tools, .nmap__zoom, .nmap__pin, .nmap__credit, .nmap__handle, .nmap__bar') == null
  );
}

/* --------------------------- placing by hand --------------------------- */

/**
 * Where a pointer is, as a coordinate.
 *
 * The inverse of everything else in this file: a client position, into the view,
 * back through the projection. This is what makes dragging a structure possible at
 * all — the pointer is in pixels and the register stores degrees.
 */
function pointerAt(
  e: PointerEvent,
  stage: HTMLElement,
  view: MapView,
  width: number,
  height: number,
): { lat: number; lng: number } {
  const box = stage.getBoundingClientRect();
  const centre = project(view.lng, view.lat, view.zoom);
  return unproject(
    centre.x + (e.clientX - box.left) - width / 2,
    centre.y + (e.clientY - box.top) - height / 2,
    view.zoom,
  );
}

/**
 * Drag the structure itself, or its rotate handle.
 *
 * One handler for both because they are the same gesture with a different sum at
 * the end: `move` follows the pointer, `rotate` takes the angle from the deck's
 * centre to the pointer. Live all the way through rather than committed on
 * release, unlike the pan — the draft is what the footprint is drawn from, so
 * every step redraws the shape under the pointer, and that IS the feedback. It is
 * one small polygon and four corners of arithmetic per frame, against a repaint of
 * a screen whose heaviest element is this map.
 *
 * Pointer capture is taken immediately here, which is safe for the same reason it
 * was not safe on the stage: this handler is on the handle itself, so there is no
 * click of anything else to swallow.
 */
function beginPlaceDrag(
  e: PointerEvent,
  kind: 'move' | 'rotate',
  view: MapView,
  width: number,
  height: number,
): void {
  const handle = e.currentTarget as HTMLElement;
  const stage = handle.closest('.nmap__stage') as HTMLElement | null;
  if (!stage || e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();

  const draft = store.get().mapPlacing;
  if (!draft) return;

  try {
    handle.setPointerCapture(e.pointerId);
  } catch {
    /* no capture available; the drag still works over the map */
  }

  const onMove = (m: PointerEvent): void => {
    const at = pointerAt(m, stage, view, width, height);
    if (kind === 'move') {
      actions.placeAt({ lat: at.lat, lng: at.lng });
      return;
    }
    /* The angle from the deck's centre to the pointer, as an axis. Read from the
       CURRENT draft centre rather than the one captured at pointerdown, so a
       rotate after a move turns about where the structure is now. */
    const centre = store.get().mapPlacing ?? draft;
    const north = (at.lat - centre.lat) * 111_320;
    const east =
      (at.lng - centre.lng) * 111_320 * Math.cos((centre.lat * Math.PI) / 180);
    if (Math.hypot(north, east) < 1) return; // on the pivot: no angle to speak of
    actions.placeAt({ bearing: normaliseAxis((Math.atan2(east, north) * 180) / Math.PI) });
  };

  const onUp = (): void => {
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', onUp);
    handle.removeEventListener('pointercancel', onUp);
  };

  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
  handle.addEventListener('pointercancel', onUp);
}

/**
 * The rotate handle: a grip at one end of the deck.
 *
 * Placed on the footprint's own end rather than at a fixed offset, so it is where
 * the thing it turns is — and it moves with the deck as it turns, which is what
 * makes it read as a grip rather than a button. Null while there is no axis yet
 * (nothing to grip) and while the deck is too small on screen to have ends.
 */
function rotateHandle(
  b: BridgeView,
  draft: MapPlacement,
  view: MapView,
  width: number,
  height: number,
): HTMLElement {
  const pin = toBox(draft.lng, draft.lat, view, width, height);
  let left = pin.left;
  let top = pin.top - 44;

  if (draft.bearing != null) {
    const corners = deckFootprint(b, { lat: draft.lat, lng: draft.lng, bearing: draft.bearing });
    if (corners) {
      // Midpoint of one end — corners are ordered end-then-end, so 0 and 1 pair up.
      const a = toBox(corners[0]![0], corners[0]![1], view, width, height);
      const c = toBox(corners[1]![0], corners[1]![1], view, width, height);
      left = (a.left + c.left) / 2;
      top = (a.top + c.top) / 2;
    }
  }

  return h('button', {
    type: 'button',
    class: 'nmap__handle nmap__handle--rotate',
    style: { left: `${left.toFixed(1)}px`, top: `${top.toFixed(1)}px` },
    /* Sits on the deck's end once there is a deck, and a short way above the pin
       before there is one — a structure with no axis (Girubari) has no end to grip,
       so the handle waits there until the first drag gives it one. */
    title:
      draft.bearing == null
        ? 'Drag away from the pin to set which way the deck runs'
        : `Drag to rotate the deck — currently ${draft.bearing.toFixed(1)}°`,
    'aria-label': 'Rotate the deck',
    onpointerdown: (e: Event) => beginPlaceDrag(e as PointerEvent, 'rotate', view, width, height),
  });
}

/** The handle that moves the whole structure: the pin, while placing. */
function moveHandle(
  draft: MapPlacement,
  view: MapView,
  width: number,
  height: number,
): HTMLElement {
  const { left, top } = toBox(draft.lng, draft.lat, view, width, height);
  return h(
    'button',
    {
      type: 'button',
      class: 'nmap__handle nmap__handle--move',
      style: { left: `${left.toFixed(1)}px`, top: `${top.toFixed(1)}px` },
      title: 'Drag to move the structure',
      'aria-label': 'Move the structure',
      onpointerdown: (e: Event) => beginPlaceDrag(e as PointerEvent, 'move', view, width, height),
    },
    icon(ICONS.map, { size: 12, stroke: 'currentColor' }),
  );
}

/**
 * The bar along the bottom of the map while a structure is being placed.
 *
 * Shows the numbers as they change, because they are what gets stored and because
 * an engineer reading a coordinate off screen can compare it with a GPS handset in
 * their other hand. Says how far it has been moved, too: "12 m" is the answer to
 * "is this a correction or am I dragging it into the next valley".
 */
function placeBar(b: BridgeView, draft: MapPlacement, s: Readonly<State>): HTMLElement {
  const moved =
    draft.from.lat != null && draft.from.lng != null
      ? Math.hypot(
          (draft.lat - draft.from.lat) * 111_320,
          (draft.lng - draft.from.lng) * 111_320 * Math.cos((draft.lat * Math.PI) / 180),
        )
      : null;
  const turned =
    draft.from.bearing != null && draft.bearing != null
      ? Math.abs(normaliseAxis(draft.bearing - draft.from.bearing + 90) - 90)
      : null;

  return h(
    'div',
    { class: 'nmap__bar' },
    h('span', { class: 'nmap__bar-id', text: b.id }),
    h('span', {
      class: 'nmap__bar-num',
      text: `${draft.lat.toFixed(6)}, ${draft.lng.toFixed(6)}`,
    }),
    h('span', {
      class: 'nmap__bar-num',
      text: draft.bearing == null ? 'no deck axis' : `${draft.bearing.toFixed(1)}° axis`,
    }),
    h('span', {
      class: 'nmap__bar-note',
      text: [
        moved == null ? 'first position' : `moved ${moved < 1 ? '<1' : moved.toFixed(0)} m`,
        turned == null || turned < 0.05 ? null : `turned ${turned.toFixed(1)}°`,
      ]
        .filter(Boolean)
        .join(' · '),
    }),
    s.mapPlaceError ? h('span', { class: 'nmap__bar-err', text: s.mapPlaceError }) : null,
    h('span', { class: 'spacer' }),
    /* Only offered once there is an axis to store. A structure dragged into place
       with no axis still has no footprint, and saving one without the other would
       be half a placement. */
    draft.bearing == null
      ? h('span', {
          class: 'nmap__bar-note',
          text: 'drag the deck handle out from the pin to set an axis',
        })
      : null,
    button('Cancel', { sm: true, disabled: s.mapPlaceSaving, onClick: () => actions.cancelPlacing() }),
    button(s.mapPlaceSaving ? 'Saving…' : 'Save position', {
      sm: true,
      primary: true,
      disabled: s.mapPlaceSaving,
      onClick: () => void actions.savePlacing(),
    }),
  );
}

/**
 * Drag to pan.
 *
 * Attached to the stage, and it moves the layers directly for as long as the
 * pointer is down — one transform on two elements, which the compositor does
 * without the main thread — then commits once on release.
 *
 * CAPTURE IS TAKEN ONLY ONCE THE DRAG STARTS, past a few pixels of slop. It has
 * to be taken at some point, or a drag that leaves the box loses its pointerup
 * and the map stays stuck to the pointer; it must not be taken before the gesture
 * is known to be a drag, or it swallows the click of whatever was pressed (see
 * startsPan). The threshold is doing double duty: it decides drag from click, and
 * it decides when capture is safe.
 */
function beginPan(e: PointerEvent, view: MapView): void {
  const stage = e.currentTarget as HTMLElement;
  if (e.button !== 0 || !startsPan(e.target as Element | null)) return;

  const startX = e.clientX;
  const startY = e.clientY;
  const layers = Array.from(stage.querySelectorAll<HTMLElement>('.nmap__pan'));
  let moved = false;

  const onMove = (m: PointerEvent): void => {
    const dx = m.clientX - startX;
    const dy = m.clientY - startY;
    if (!moved && Math.hypot(dx, dy) < 3) return;
    if (!moved) {
      moved = true;
      // Now that this is a drag, keep the rest of it — including the pointerup —
      // coming here even if the pointer leaves the map.
      try {
        stage.setPointerCapture(m.pointerId);
      } catch {
        /* no capture available; the drag still works inside the box */
      }
      stage.classList.add('nmap__stage--panning');
    }
    for (const l of layers) l.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const onUp = (u: PointerEvent): void => {
    stage.removeEventListener('pointermove', onMove);
    stage.removeEventListener('pointerup', onUp);
    stage.removeEventListener('pointercancel', onUp);
    stage.classList.remove('nmap__stage--panning');
    // Nothing was ever translated, so there is nothing to hand back.
    if (!moved) return;

    /* Committed against the view this gesture STARTED from, captured when the
       pointer went down. Reading it from the store here would be reading a value
       a repaint mid-drag could have changed under us. */
    actions.mapMoved(panned(view, u.clientX - startX, u.clientY - startY));

    /* The transform is deliberately LEFT IN PLACE. Clearing it here would put the
       map back where the drag started for the one frame before the repaint draws
       it where the drag ended — a visible snap backwards at the end of every pan.
       The repaint clears it instead: the layers it builds carry no `style`, and
       src/core/dom.ts removes attributes the new element does not have. The commit
       above always changes `mapView` (a new object, and a moved drag by
       definition moved), so that repaint always comes. */
  };

  stage.addEventListener('pointermove', onMove);
  stage.addEventListener('pointerup', onUp);
  stage.addEventListener('pointercancel', onUp);
}

/**
 * Wheel to zoom, about the cursor.
 *
 * Quarter-level steps rather than whole ones: the fitted home view is fractional
 * anyway, and a trackpad's many small deltas over one gesture then read as a
 * smooth zoom instead of a series of jumps. `preventDefault` stops the register
 * scrolling behind the map, which is the whole reason the handler is not passive.
 */
function onWheel(
  e: WheelEvent,
  view: MapView,
  width: number,
  height: number,
  base: MapBase,
): void {
  e.preventDefault();
  const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const anchor = { x: e.clientX - box.left, y: e.clientY - box.top };
  const delta = e.deltaY > 0 ? -0.25 : 0.25;
  actions.mapMoved(zoomed(view, delta, anchor, width, height, base));
}

/**
 * Keep `mapSize` in step with the box on screen.
 *
 * Called from paint() AFTER the render, and it has to be: src/core/dom.ts patches
 * the live tree from a freshly built one rather than swapping it in, so the
 * element `nepalMapCard` returns is a template that is thrown away — measuring it
 * would measure a node that was never in the document. The stage that IS on
 * screen is found here, by selector, once the render is over.
 *
 * The same reason src/modeler/viewport.ts is reconciled from there rather than
 * from a screen: anything that needs a box has to wait for the box to exist.
 *
 * One observer for the life of the page, re-pointed at whatever stage is mounted
 * — the map is one card on one screen, so there is never more than one. Absent in
 * jsdom, where nothing has a size and the store's default stands, which is what
 * lets the tests render this map at all.
 */
let sizeObserver: ResizeObserver | null = null;
let observed: Element | null = null;

export function syncNepalMap(): void {
  const stage = document.querySelector<HTMLElement>('.nmap__stage');

  if (!stage) {
    // Off the network screen. Stop watching, but leave `mapSize` alone: it is
    // still the last true measurement, and clearing it would make the next
    // arrival draw one frame from a guess.
    if (observed && sizeObserver) sizeObserver.unobserve(observed);
    observed = null;
    return;
  }

  syncTiles();
  if (stage.clientWidth > 0) actions.mapResized(stage.clientWidth, stage.clientHeight);

  if (typeof ResizeObserver === 'undefined' || observed === stage) return;
  sizeObserver ??= new ResizeObserver((entries) => {
    const box = entries[0]?.contentRect;
    if (box && box.width > 0) actions.mapResized(box.width, box.height);
  });
  if (observed) sizeObserver.unobserve(observed);
  sizeObserver.observe(stage);
  observed = stage;
}

/* --------------------------- the card --------------------------- */

/**
 * Which base map is drawn — one button per source, the current one pressed.
 *
 * Two buttons rather than one that toggles: a toggle has to be read to find out
 * what it will do, and a pair shows what you are looking at as well as what else
 * there is. `aria-pressed` is the same statement for a screen reader.
 */
function baseSwitch(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'nmap__bases', role: 'group', 'aria-label': 'Base map' },
    ...(Object.keys(TILE_SOURCES) as MapBase[]).map((id) => {
      const source = TILE_SOURCES[id];
      const on = s.mapBase === id;
      return h(
        'button',
        {
          type: 'button',
          class: ['nmap__btn', 'nmap__btn--base', on && 'nmap__btn--on'],
          'aria-pressed': on ? 'true' : 'false',
          title: source.title,
          onclick: () => actions.mapBaseSelected(id),
        },
        source.label,
      );
    }),
  );
}

/**
 * Flat map, or the selected structure on its ground.
 *
 * Beside the base switch and built the same way, because it is the same kind of
 * choice: what you are looking at, not where. 3D is refused for a structure with
 * no position — there is nothing to stand a terrain patch on — and says so rather
 * than offering a view it cannot build.
 */
function modeSwitch(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  /* An empty register has no structure to stand on, so nothing is placeable and
     the two views that need one are refused for the same reason a structure with
     no coordinates refuses them — with a sentence rather than silently. */
  const placeable = b !== null && b.lat != null && b.lng != null;
  const subject = b === null ? 'No structure is selected, so there' : `${b.id} has no position, so there`;

  return h(
    'div',
    { class: 'nmap__bases', role: 'group', 'aria-label': 'Map mode' },
    ...(['2d', '3d', 'fly'] as const).map((mode) => {
      const on = s.mapMode === mode;
      const label = mode === '2d' ? 'Flat' : mode === '3d' ? 'Site' : 'Fly';
      /* Both 3D modes need somewhere to stand: the Site view builds ground around
         one structure, and the fly-around map has nothing to fly to. Refused with a
         reason rather than offered and then empty. */
      const needsPosition = mode !== '2d';
      /* And both need the network, which the flat map does not: their elevation
         comes through the register's own terrain endpoint and there is no
         bundled fallback for ground the way there is for the country outline.
         Said here rather than left to degrade into an empty scene — an inspector
         offline in a valley should be told the view cannot be built, not watch
         it fail to build.

         (The endpoint is deliberately not named as a path anywhere in this file:
         a check in scripts/smoke.ts asserts the map never routes its raster
         tiles through the Worker, and it does that by looking for the string.) */
      const needsNetwork = mode !== '2d' && s.backend !== 'live';
      return h(
        'button',
        {
          type: 'button',
          class: ['nmap__btn', 'nmap__btn--base', on && 'nmap__btn--on'],
          'aria-pressed': on ? 'true' : 'false',
          disabled: (needsPosition && !placeable) || needsNetwork ? true : undefined,
          title:
            mode === '2d'
              ? 'The flat map, with every structure on it — the only mode that works offline'
              : needsNetwork
                ? 'Needs the backend: elevation is served by this deployment and has no offline fallback'
                : !placeable
                ? `${subject} is no ground to put it on`
                  : mode === '3d'
                    ? `${b?.id ?? ''} on its ground — elevation tiles, orbit to look along the crossing`
                    : 'Every structure extruded on terrain — tip it over and fly along the road',
          onclick: () => actions.mapModeSelected(mode),
        },
        label,
      );
    }),
  );
}

/**
 * Plain pins, or pins coloured by screening band.
 *
 * Only on the flat map. The Site view draws one structure, so there is nothing
 * to compare it against, and the fly-around map extrudes decks rather than
 * drawing pins — neither has anything for this to colour.
 */
function paintSwitch(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'nmap__bases', role: 'group', 'aria-label': 'Pin colour' },
    ...(['plain', 'screening'] as MapPaint[]).map((paint) => {
      const on = s.mapPaint === paint;
      return h(
        'button',
        {
          type: 'button',
          class: ['nmap__btn', 'nmap__btn--base', on && 'nmap__btn--on'],
          'aria-pressed': on ? 'true' : 'false',
          title:
            paint === 'plain'
              ? 'One colour for every structure — where the network is'
              : 'Colour each pin by its flood, scour and seismic screening. Grey is not screened, which is not the same as fine',
          onclick: () => actions.mapPaintSelected(paint),
        },
        paint === 'plain' ? 'Plain' : 'Screening',
      );
    }),
  );
}

/**
 * What the colours mean, and how many pins are in each.
 *
 * Under the map rather than floating over it, and it carries the counts as well
 * as the swatches. A legend that only named the bands would invite a reader to
 * assume the grey pins are a rounding error; the count is what says they are
 * most of what is on screen.
 *
 * **It counts the LOCATED structures, not the register.** A legend under a map
 * has to describe the map: counting records with no coordinates would put
 * structures in the tally that have no pin to find, and the card head above
 * already reports how many are missing. The register-wide distribution is the
 * overview's card, where it belongs.
 */
function screeningLegend(list: readonly BridgeView[]): HTMLElement {
  const counts = bandCounts(located(list));
  return h(
    'div',
    { class: 'nmap__legend' },
    ...BANDS.map((band) =>
      h(
        'span',
        { class: 'nmap__legend-item' },
        h('span', { class: `nmap__legend-dot nmap__legend-dot--${band}`, 'aria-hidden': 'true' }),
        h('span', { text: `${bandLabel(band)} ${counts[band]}` }),
      ),
    ),
    h('span', {
      class: 'nmap__legend-note',
      text: 'Worst of flood, scour and seismic. Grey means the register does not hold what the screen needs — not that the structure is fine.',
    }),
  );
}

/**
 * The toolbar across the top-left of the map: what you are looking at, as one
 * bar of switches, with any actions beside it. Shared by all three modes so the
 * switches stay where they were when the view changes under them.
 */
function toolbar(s: Readonly<State>, ...actionsBeside: (HTMLElement | null)[]): HTMLElement {
  return h(
    'div',
    { class: 'nmap__controls', role: 'toolbar', 'aria-label': 'Map' },
    h(
      'div',
      { class: 'nmap__tools' },
      modeSwitch(s),
      baseSwitch(s),
      s.mapMode === '2d' ? paintSwitch(s) : null,
    ),
    ...actionsBeside,
  );
}

/** The flat map's toolbar: the switches, then retrying tiles and placing. */
function controls(s: Readonly<State>, view: MapView): HTMLElement {
  return toolbar(
    s,
    s.mapTiles === 'failed'
      ? h(
          'button',
          {
            type: 'button',
            class: 'nmap__btn nmap__btn--action',
            title: 'Try the tile server again',
            onclick: () => actions.mapRetryTiles(),
          },
          icon(ICONS.refresh),
          'Retry tiles',
        )
      : null,
    placeButton(s, view),
  );
}

/** Zoom in / out, the register and the country: a stack in the bottom-right corner. */
function zoomStack(s: Readonly<State>, view: MapView, width: number, height: number): HTMLElement {
  const step = (delta: number) => (): void =>
    actions.mapMoved(zoomed(view, delta, { x: width / 2, y: height / 2 }, width, height, s.mapBase));
  /* The department's own country, not a hard-coded one: the button fits the
     jurisdiction, so it is named for the jurisdiction. `countryName` and not the
     agency's `government` line, which is too long for a tooltip. */
  const country = active().countryName;
  const zoomBtn = (label: string, title: string, d: Parameters<typeof icon>[0], onclick: () => void) =>
    h(
      'button',
      { type: 'button', class: 'nmap__btn nmap__btn--icon', title, 'aria-label': label, onclick },
      icon(d, { size: 16 }),
    );

  return h(
    'div',
    { class: ['nmap__zoom', s.mapPlacing && 'nmap__zoom--raised'], role: 'group', 'aria-label': 'Zoom' },
    zoomBtn('Zoom in', 'Zoom in', ICONS.plus, step(1)),
    zoomBtn('Zoom out', 'Zoom out', ICONS.minus, step(-1)),
    zoomBtn('Fit', 'Fit — back to the structures this register covers', ICONS.fit, () => actions.mapHome()),
    zoomBtn(country, `${country} — the whole country`, ICONS.globe, () =>
      actions.mapMoved(fitCountry(width, height)),
    ),
  );
}

/**
 * "Adjust position" — the way into placing the selected structure by hand.
 *
 * Only offered close in, and that is a judgement rather than a technicality: the
 * point of dragging a structure is to line it up with a crossing you can see, and
 * at country zoom a pixel is 400 m. Below the footprint zoom there is nothing on
 * screen precise enough to place anything against, so the button says why instead
 * of pretending otherwise.
 */
function placeButton(s: Readonly<State>, view: MapView): HTMLElement | null {
  if (s.mapPlacing) return null;
  /* `inventory.edit`, the same permission as correcting the record: a position is
     inventory, and moving a structure changes what every other screen draws. Not
     offered at all rather than disabled — the map already has one disabled state
     for this button, the zoom one above, and two different reasons wearing the
     same grey would make the zoom message unreadable. */
  if (!can(s, 'inventory.edit')) return null;
  const b = findBridge(s.selectedId);
  /* Nothing to move. Withheld rather than disabled, the same way this button is
     withheld from a role that may not edit: an empty register has no structure
     whose position could be adjusted, so the control has nothing to explain. */
  if (b === null) return null;
  const close = view.zoom >= DECK_MIN_ZOOM;

  return h(
    'button',
    {
      type: 'button',
      class: 'nmap__btn nmap__btn--action',
      disabled: close ? undefined : true,
      title: close
        ? `Move ${b.id} to where it actually is, and turn its deck to match the crossing`
        : 'Zoom in to adjust a position — there is nothing to line a structure up against from here',
      onclick: () =>
        actions.startPlacing(b, { lat: view.lat, lng: view.lng }),
    },
    icon(ICONS.move),
    'Adjust position',
  );
}

/**
 * Whose map this is, in the corner of it.
 *
 * Rendered from the source in force rather than written out once, because the
 * credit is part of the licence and the licence changes with the tiles: OSM's
 * standard style asks for attribution, and OpenTopoMap's is CC-BY-SA, which
 * requires naming the style and its licence as well as the data. With no tiles at
 * all there is nothing of theirs on screen to credit, and the line says what IS
 * being shown instead.
 */
function credit(s: Readonly<State>): HTMLElement {
  if (s.mapTiles === 'failed') {
    return h(
      'div',
      { class: 'nmap__credit' },
      h('span', { text: 'Tiles unavailable — outline only (Natural Earth, public domain)' }),
    );
  }

  const parts = sourceOf(s.mapBase).credit;
  return h(
    'div',
    { class: 'nmap__credit' },
    ...parts.flatMap((part, i) => [
      i === 0 ? null : h('span', { class: 'nmap__credit-sep', text: ' | ' }),
      part.href
        ? h('a', { href: part.href, target: '_blank', rel: 'noreferrer', text: part.text })
        : h('span', { text: part.text }),
    ]),
  );
}

/**
 * The fly-around card: a slot MapLibre mounts into, and what it has to say.
 *
 * As empty as the Site view's, and for the same reason — everything inside belongs
 * to a library holding a WebGL context, and a reconciler must not touch it. See
 * src/map/flyMap.ts.
 */
function flyCard(s: Readonly<State>): HTMLElement {
  const fly = flyStatus();

  return card(
    { clip: true, class: 'nmap' },
    h(
      'div',
      { class: 'nmapfly' },
      /* `data-keep`: MapLibre's canvas goes in here after the render, and the
         reconciler must leave it alone — see KEEP in src/core/dom.ts. */
      h('div', { class: 'nmapfly__slot', 'data-keep': 'fly' }),
      toolbar(s),
      h(
        'div',
        { class: 'nmap__credit' },
        ...sourceOf(s.mapBase).credit.flatMap((part, i) => [
          i === 0 ? null : h('span', { class: 'nmap__credit-sep', text: ' | ' }),
          h('span', { text: part.text }),
        ]),
        h('span', { class: 'nmap__credit-sep', text: ' | ' }),
        h('span', { text: 'elevation: Mapzen terrarium / SRTM · MapLibre GL' }),
      ),
      fly.state === 'loading' || fly.state === 'idle'
        ? h(
            'div',
            { class: 'nmap3d__cover' },
            h('div', { class: 'mdl__spinner' }),
            h('div', { class: 'nmap3d__cover-title', text: 'Loading the map engine…' }),
            h('div', {
              class: 'nmap3d__cover-sub',
              text: 'MapLibre GL and the terrain tiles — about a megabyte, once per session',
            }),
          )
        : null,
      fly.state === 'failed'
        ? h('div', { class: 'nmap3d__cover' }, h('div', { class: 'nmap3d__cover-title', text: fly.message }))
        : null,
    ),
  );
}

/**
 * What the 3D view has to say about the geometry it is drawing, if anything.
 *
 * A pure function rather than four branches inside the card, and for the reason
 * this project has had to learn three times now: the card only renders once the
 * scene reports ready, which needs WebGL, which the tests do not have. Written
 * inline, the whole of `GR-16`'s user-visible behaviour would have been
 * unreachable by any check — and the one thing the item exists to prevent is a
 * view that is confidently wrong.
 *
 * Null means say nothing. A current export needs no bar, and a bar that is always
 * there is a bar nobody reads.
 */
export interface GeometryNotice {
  text: string;
  /** Label for the button beside it, or null while an export is running. */
  action: string | null;
  actionTitle: string;
  /**
   * Whether this is a warning rather than an offer.
   *
   * Only `stale` is. `none` is an invitation to build something, and `unknown` is
   * an admission — neither is a statement that what is on screen is wrong, and
   * colouring them as if they were would make the one that IS wrong unremarkable.
   */
  warn: boolean;
}

export function geometryNotice(
  model: { name: string; geometryState: ModelSummary['geometryState'] } | undefined,
  building: boolean,
): GeometryNotice | null {
  if (!model) return null;
  if (model.geometryState === 'fresh') return null;

  const exporting = {
    text: `Exporting ${model.name} — running the modeller out of sight…`,
    action: null,
    actionTitle: '',
    warn: model.geometryState === 'stale',
  };
  if (building) return exporting;

  switch (model.geometryState) {
    case 'stale':
      return {
        text: `${model.name} has been edited since this geometry was exported. What is on screen is the older structure.`,
        action: 'Re-export',
        actionTitle: `Export ${model.name} again so this view matches the model`,
        warn: true,
      };
    case 'unknown':
      return {
        text: `This geometry was exported before the register recorded what an export came from, so it cannot be confirmed as current.`,
        action: 'Re-export',
        actionTitle: `Export ${model.name} again so this view can be confirmed against the model`,
        warn: false,
      };
    case 'none':
      return {
        text: 'Drawn as a box at the recorded length and width. The modeller has the real geometry.',
        action: 'Build geometry',
        actionTitle: `Export ${model.name} from the modeller and draw it here instead of a box`,
        warn: false,
      };
  }
}

/**
 * The 3D card: a slot the scene mounts into, and what it has to say.
 *
 * Almost empty on purpose. Everything inside the slot is three.js's — a canvas, a
 * WebGL context, uploaded geometry and textures — and none of it can survive being
 * rebuilt by a reconciler, so the slot stays empty here and src/map/scene3d.ts puts
 * its canvas in after the render. Same arrangement as the modeller's viewport, for
 * the same reason.
 */
function sceneCard(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  /* Every line below this is about one structure — the ground under it, the
     model standing on it, the freeboard it was recorded at. An empty register
     has none, so the card says that instead of building a scene around a record
     that is not there. main.ts tears the WebGL context down on the same
     condition, so there is nothing running behind this. */
  if (b === null) {
    return card(
      { clip: true },
      cardHead('Where the network is', 'Nothing to stand a scene on'),
      h('div', { class: 'nmap__foot' }, h('span', {
        text: 'This register has no structures in it yet, so there is no crossing to draw on its ground.',
      })),
    );
  }
  const scene = sceneStatus();
  /* The model whose geometry the scene is drawing, or would draw. Same choice the
     record's hero makes, through the same function so it stays the same choice —
     see heroModel — so the two views of a structure are of the same model. The
     fallback keeps a model with nothing in it nameable, which is what lets
     geometryNotice say so rather than say nothing. */
  const model = heroModel(s.models.models) ?? s.models.models[0];
  const building = s.geometryBuild?.bridgeId === b.id;
  const notice = geometryNotice(model, building);

  return card(
    { clip: true, class: 'nmap' },
    h(
      'div',
      { class: 'nmap3d' },
      /* `data-keep` for the same reason as the fly view's slot: the scene's WebGL
         canvas is mounted here after the render and is not the reconciler's to
         patch — see KEEP in src/core/dom.ts. */
      h('div', { class: 'nmap3d__slot', 'data-keep': 'scene' }),
      // The controls ride over the scene exactly as they do over the map, so the
      // two modes are the same object seen two ways.
      toolbar(s),
      h(
        'div',
        { class: 'nmap__credit' },
        ...sourceOf(s.mapBase).credit.flatMap((part, i) => [
          i === 0 ? null : h('span', { class: 'nmap__credit-sep', text: ' | ' }),
          h('span', { text: part.text }),
        ]),
        h('span', { class: 'nmap__credit-sep', text: ' | ' }),
        h('span', { text: 'elevation: Mapzen terrarium / SRTM' }),
      ),
      scene.state === 'loading' || scene.state === 'idle'
        ? h(
            'div',
            { class: 'nmap3d__cover' },
            h('div', { class: 'mdl__spinner' }),
            h('div', { class: 'nmap3d__cover-title', text: `Building ${b.name}'s ground…` }),
            h('div', {
              class: 'nmap3d__cover-sub',
              text: 'Elevation tiles and imagery, then the deck at its recorded size',
            }),
          )
        : null,
      scene.state === 'failed'
        ? h('div', { class: 'nmap3d__cover' }, h('div', { class: 'nmap3d__cover-title', text: scene.message }))
        : null,
      /* The offer to build real geometry, and it is an offer rather than something
         done quietly: it drives a whole modeller in a hidden frame for a few
         seconds. */
      /* What the scene is drawing, and whether it is still true — the whole of
         `GR-16` on screen. The decision is geometryNotice() above, because this
         branch only runs once the scene reports ready and nothing in CI can get
         it there. */
      notice && scene.state === 'ready'
        ? h(
            'div',
            { class: ['nmap3d__bar', notice.warn && 'nmap3d__bar--stale'] },
            h('span', { class: 'nmap3d__bar-note', text: notice.text }),
            h('span', { class: 'spacer' }),
            s.geometryError ? h('span', { class: 'nmap__bar-err', text: s.geometryError }) : null,
            notice.action === null || model === undefined
              ? h('div', { class: 'mdl__spinner mdl__spinner--sm' })
              : button(notice.action, {
                  sm: true,
                  primary: true,
                  title: notice.actionTitle,
                  onClick: () => void buildModelGeometry(b.id, model.id),
                }),
          )
        : null,
    ),
    h(
      'div',
      { class: 'nmap__foot' },
      icon(ICONS.cube, { size: 13, stroke: 'var(--txt3)' }),
      h('span', {
        /* The vertical is the thing to be honest about, so it is said here rather
           than in a tooltip — see the note at the top of src/map/terrain.ts. And
           when the real geometry is on screen, the height it comes out at is worth
           saying beside the recorded freeboard: the model knows its pier heights,
           the record states a clearance, and the two are independent. */
        text:
          scene.groundElevation == null
            ? 'Heights are relative to the ground under the pin. The deck is drawn at its recorded freeboard, not at its recorded level.'
            : `Ground under ${b.id} reads ${scene.groundElevation.toFixed(0)} m in the elevation data, against ` +
              `${b.deckRL == null ? 'no recorded deck level' : `a recorded deck level of ${b.deckRL.toFixed(2)} m`} — ` +
              'the two datums do not agree, so heights here are relative to the ground. ' +
              (scene.source === 'model'
                ? `The modeller's own geometry stands on that ground and comes out ${scene.deckHeight?.toFixed(1) ?? '?'} m tall, ` +
                  `against a recorded freeboard of ${b.freeboard == null ? 'none' : `${b.freeboard.toFixed(2)} m`}.`
                : `The deck is a box at the recorded size, at ${b.freeboard == null ? 'an assumed 8 m' : 'its recorded freeboard'}.`),
      }),
    ),
  );
}

/**
 * The map card for the network screen.
 *
 * Everything inside is rebuilt on every paint and reconciled into place by
 * src/core/dom.ts, which patches rather than replaces — so an unchanged tile
 * keeps its `src`, its already-decoded image and its position, and a repaint
 * costs no requests. That is the property that makes a declarative map viable
 * here at all.
 */
export function nepalMapCard(s: Readonly<State>, list: readonly BridgeView[]): HTMLElement {
  if (s.mapMode === 'fly') return flyCard(s);
  if (s.mapMode === '3d') return sceneCard(s);
  const { width, height } = s.mapSize;
  const view = currentView(s, list);
  const offline = s.mapTiles === 'failed';
  const draft = s.mapPlacing;

  /* A structure being placed is drawn from the draft, so it needs a pin even if
     the record has no position yet — that is exactly the Girubari case. It gets
     the move handle instead of a marker, so the two are filtered apart here. */
  const pins = located(list).filter((b) => b.id !== draft?.bridgeId);
  const placed = draft ? findBridge(draft.bridgeId) : null;

  const stage = h(
    'div',
    {
      class: ['nmap__stage', offline && 'nmap__stage--offline', draft && 'nmap__stage--placing'],
      onpointerdown: (e: Event) => beginPan(e as PointerEvent, view),
      onwheel: (e: Event) => onWheel(e as WheelEvent, view, width, height, s.mapBase),
    },
    // Both layers carry `nmap__pan`, which is what beginPan translates: the
    // markers ride with the tiles because they are inside the second one.
    offline ? null : h('div', { class: 'nmap__pan' }, tileLayer(s.mapBase, view, width, height)),
    h(
      'div',
      { class: 'nmap__pan' },
      vectorLayer(s, placed ? [...pins, placed as never] : pins, view, width, height),
      ...pins.map((b) => marker(b, s, view, width, height)),
      ...(draft && placed
        ? [moveHandle(draft, view, width, height), rotateHandle(placed, draft, view, width, height)]
        : []),
    ),
    controls(s, view),
    zoomStack(s, view, width, height),
    credit(s),
    draft && placed ? placeBar(placed, draft, s) : null,
  );

  /* No title and no footnote: the map fills the top of the panel edge to edge,
     and the panel header already names it. */
  return card(
    { clip: true, class: 'nmap' },
    stage,
    s.mapPaint === 'screening' ? screeningLegend(list) : null,
  );
}

/* `panned`, `zoomed`, `toBox` and `tileUrl` are exported above as ordinary
   functions rather than hidden behind the card: they are the arithmetic a pointer
   gesture turns into, and scripts/smoke.ts checks them directly — a pan that
   moves the wrong way, or a zoom that does not hold its anchor, is not something
   a jsdom click could catch. */
